// UppyDashboardWidget.tsx
import { useEffect, useRef, useState } from 'react'
import '@uppy/core/css/style.min.css'
import '@uppy/dashboard/css/style.min.css'
import Dashboard from '@uppy/dashboard'
import { CloudArrowUpIcon } from '@heroicons/react/24/solid'
import { useUppy } from '../../Provider/UppyProvider.js'
import { TooltipTrigger } from '../../Provider/TooltipManager.js'
import { UploadedFileList } from './UploadedFileList.js'

export const UppyDashboardWidget = () => {
  const { uppy, meta, setMeta, poolIsLoading } = useUppy()
  // The completed-file list for the target pool is fetched async whenever `meta` changes
  // (see UppyProvider). Until it arrives, onBeforeFileAdded can't dedup and rejects files with
  // "skipping file for safety". Block the drop area during that window instead of letting the
  // user hit that error.
  const isPoolLoading = meta?.poolId ? poolIsLoading(meta.poolId) : false
  const dashboardRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  // Armed = the next file added should pop the drawer open. Starts armed so the very first
  // upload opens it, and is re-armed once a whole batch finishes (Uppy's `complete` event).
  const [isArmed, setIsArmed] = useState(true)
  const isArmedRef = useRef(isArmed)
  // Uploads are started from the order pages and open the drawer themselves, so the trigger only
  // shows while files are still going up.
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    if (!uppy) return
    const recount = () => setPendingCount(uppy.getFiles().filter((x) => !x.progress?.uploadComplete).length)
    const events = ['file-added', 'file-removed', 'upload-success', 'upload-error', 'complete', 'cancel-all'] as const
    recount()
    events.forEach((e) => uppy.on(e, recount))
    return () => events.forEach((e) => uppy.off(e, recount))
  }, [uppy])

  useEffect(() => {
    isArmedRef.current = isArmed
  }, [isArmed])

  useEffect(() => {
    if (!uppy) return
    const dashboardPlugin = uppy.getPlugin('Dashboard')
    if (!dashboardPlugin) {
      uppy.use(Dashboard, {
        inline: true,
        target: dashboardRef.current!,
        hideProgressDetails: false,
        proudlyDisplayPoweredByUppy: false,
        height: 400,
        hideUploadButton: true,
        showRemoveButtonAfterComplete: false
      })
    }

    // Pop the drawer open as soon as a fresh batch starts, so progress is visible while files
    // are still uploading. Only once per batch, so a user who closes it mid-upload isn't fought.
    const onFileAdded = () => {
      if (isArmedRef.current) {
        setIsArmed(false)
        setIsOpen(true)
      }
    }
    // Whole batch settled — arm again so the next upload re-opens the drawer.
    const onComplete = () => setIsArmed(true)

    uppy.on('file-added', onFileAdded)
    uppy.on('complete', onComplete)

    return () => {
      if (dashboardPlugin) {
        uppy.removePlugin(dashboardPlugin)
      }
      uppy.off('file-added', onFileAdded)
      uppy.off('complete', onComplete)
    }
  }, [uppy, dashboardRef])

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && uppy && pendingCount > 0 && (
        <button id="tour-upload-button" data-testid="uploads-button" className="btn flex gap-2 mr-4" onClick={() => setIsOpen(true)}>
          <CloudArrowUpIcon className="w-5 h-5" /> {pendingCount} Uploads
        </button>
      )}

      {/* Sliding drawer */}
      <div
        className={`z-60 fixed flex flex-col right-0 top-0 h-full w-full max-w-xl bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* gap, not just justify-between: at this drawer's width the row runs out of free space to
            distribute, and the item-type select ends up flush against Close. */}
        <div className="flex justify-between items-center gap-3 p-4 border-b">
          <h2 className="text-lg font-semibold">Upload Files</h2>
          {meta && (
            <>
              <div className="text-sm text-gray-700 flex flex-col">
                <TooltipTrigger
                  tooltip={
                    <>
                      <div className={'flex flex-col'}>
                        <span>
                          Order <strong>{meta.orderId}</strong>
                        </span>
                        <span>
                          Dataset <strong>{meta.poolId}</strong>
                        </span>
                      </div>
                    </>
                  }
                >
                  <span>Uploading to current order and pool</span>
                </TooltipTrigger>
              </div>
              <select
                value={meta.itemType}
                onChange={(e) =>
                  setMeta({
                    orderId: meta.orderId,
                    poolId: meta.poolId,
                    itemType: e.target.value as 'SAMPLE' | 'NEGATIVE_CONTROL',
                    isMerged: meta?.isMerged,
                    isTrimmed: meta?.isTrimmed
                  })
                }
                className="select select-sm"
              >
                <option value="SAMPLE">Sample</option>
                <option value="NEGATIVE_CONTROL">Negative Control</option>
              </select>
            </>
          )}
          <button data-testid="close-uppy" className="btn btn-sm btn-outline" onClick={() => setIsOpen(false)}>
            Close
          </button>
        </div>
        <div className={`relative p-4 min-h-[500px] overflow-y-auto ${!meta ? 'hidden' : ''}`}>
          <div ref={dashboardRef} />
          {isPoolLoading && (
            <div
              data-testid="uppy-pool-loading"
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/80 cursor-wait"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 animate-spin text-gray-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              <span className="text-sm text-gray-600">Preparing pool, one moment…</span>
            </div>
          )}
        </div>
        <UploadedFileList />
      </div>
    </>
  )
}
